/**
 * Acumatica coding rules the canvas can apply while the reviewer codes, from data
 * the backend already sends in glInfo. They mirror the backend's pre-send checks
 * (A16 / A07 in the connector's preflight) so the reviewer meets them while coding,
 * not at Send to ERP. Everything else Acumatica checks is asked of the backend when
 * the document is approved (validateErpDocument).
 *
 * - A line on a project must post to a GL account mapped to an ACCOUNT GROUP
 *   (glInfo.projectGlAccounts: their codes). Acumatica refuses any other.
 *   `null` means the backend does not know yet (accounts synced before it sent the
 *   field) — then nothing is narrowed or flagged.
 * - A line on a project needs a cost code, once the company has cost codes at all.
 */

/** The code half of a "<code> - <name>" dropdown label. */
export const codeOfLabel = (label) =>
  String(label ?? '')
    .trim()
    .split(' - ')[0]
    .trim();

export const isBlank = (v) => v === undefined || v === null || String(v).trim() === '' || v === '-';

/** What the rules need, read once off glInfo. */
export const acumaticaCodingRules = (glInfo) => ({
  projectGlAccounts: Array.isArray(glInfo?.projectGlAccounts) ? glInfo.projectGlAccounts : null,
  usesCostCodes: Array.isArray(glInfo?.costIds) && glInfo.costIds.length > 0
});

/** Whether this GL account may sit on a project line. Unknown ⇒ yes. */
export const glAccountTakesProject = (glLabel, rules) => {
  if (isBlank(glLabel) || !Array.isArray(rules?.projectGlAccounts)) return true;
  return rules.projectGlAccounts.includes(codeOfLabel(glLabel));
};

/** The GL dropdown for a line (or header) that carries a project. */
export const projectGlOptions = (options, rules) => {
  if (!Array.isArray(options) || !Array.isArray(rules?.projectGlAccounts)) return options;
  const allowed = new Set(rules.projectGlAccounts);
  return options.filter((o) => allowed.has(codeOfLabel(o)));
};

export const ACUMATICA_GL_NOT_FOR_PROJECT = (glLabel) =>
  `GL account ${codeOfLabel(
    glLabel
  )} has no account group in Acumatica, so it can't be used on a project line. Pick a project cost account or remove the project`;
export const ACUMATICA_COST_CODE_REQUIRED = 'A line coded to a project needs a cost code';

/**
 * The coding problems a project line has, by field. `fields` is the line's
 * effective coding (own value, else the header's): jobId, glAccount, costId.
 */
export const acumaticaProjectLineIssues = (fields, rules) => {
  const issues = {};
  if (isBlank(fields?.jobId)) return issues;
  if (!glAccountTakesProject(fields?.glAccount, rules)) {
    issues.GlAccount = ACUMATICA_GL_NOT_FOR_PROJECT(fields?.glAccount);
  }
  if (rules?.usesCostCodes && isBlank(fields?.costId)) {
    issues.userCostId = ACUMATICA_COST_CODE_REQUIRED;
  }
  return issues;
};
